import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../config/supabase'
import LoadingSpinner from '../LoadingSpinner'
import { useAuth } from '../../contexts/SupabaseAuthContext'

const AuthCallback = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [status, setStatus] = useState<string>('Inicializando verificação...')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    
    // Listener de segurança para capturar login via evento (mais rápido que polling)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        console.log('⚡ [AuthCallback] Evento SIGNED_IN detectado! Redirecionando...')
        if (mounted) {
          // Recuperar URL de retorno salva antes do login
          const returnTo = window.localStorage.getItem('postLoginReturn') || '/'
          window.localStorage.removeItem('postLoginReturn')
          
          // Pequeno delay para garantir que o estado global propagou
          setTimeout(() => navigate(returnTo, { replace: true }), 100)
        }
      }
    })

    const handleAuthCallback = async () => {
      try {
        console.log('🔄 [AuthCallback] Iniciando verificação...')
        
        // 0. FAST PATH: Verificar se já estamos logados via Contexto
        if (user) {
           console.log('🚀 [AuthCallback] Usuário já identificado no contexto. Redirecionando...')
           const returnTo = window.localStorage.getItem('postLoginReturn') || '/'
           window.localStorage.removeItem('postLoginReturn')
           navigate(returnTo, { replace: true })
           return
        }

        // 1. Aguardar Supabase processar URL (Auto-detect)
        // Aumentado para 1000ms para dar tempo ao cliente Supabase de processar o hash e persistir a sessão
        await new Promise(r => setTimeout(r, 1000))
        
        if (!mounted) return

        setStatus('Analisando credenciais...')

        // 2. TENTATIVA PRIORITÁRIA: Parsing Manual do Hash
        // Em ambientes como Trae Preview ou Localhost, o cliente Supabase pode falhar em capturar o hash automaticamente.
        const hash = window.location.hash
        
        // Verificar erros retornados pelo provedor (Google/Supabase)
         if (hash && hash.includes('error=')) {
            const params = new URLSearchParams(hash.substring(1))
            const errorCode = params.get('error')
            const errorDesc = params.get('error_description') || 'Erro desconhecido durante o login social.'
            console.error(`❌ [AuthCallback] Erro retornado na URL: ${errorCode} - ${errorDesc}`)
            setError(errorDesc.replace(/\+/g, ' '))
            return
         }

        if (hash && hash.includes('access_token')) {
          console.log('🔑 [AuthCallback] Hash detectado. Tentando extração manual...')
          setStatus('Processando token de acesso...')
          
          // Extrair tokens do hash manualmente
          const params = new URLSearchParams(hash.substring(1)) // Remove o '#'
          const access_token = params.get('access_token')
          const refresh_token = params.get('refresh_token')
          
          if (access_token && refresh_token) {
            console.log('✅ [AuthCallback] Tokens extraídos. Definindo sessão...')
            
            // Race condition protection: timeout para setSession
            const setSessionPromise = supabase.auth.setSession({
              access_token,
              refresh_token
            })
            
            // Timeout de 5s
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Timeout ao definir sessão')), 5000)
            )

            try {
              const result = await Promise.race([setSessionPromise, timeoutPromise]) as any
              const { data, error: setSessionError } = result

              if (setSessionError) {
                console.error('❌ [AuthCallback] Erro ao definir sessão manual:', setSessionError)
                throw setSessionError
              }

              if (data.session) {
                console.log('🚀 [AuthCallback] Sessão definida com sucesso! Redirecionando...')
                setStatus('Autenticação concluída! Entrando...')
                // Limpar hash para limpeza visual
                window.history.replaceState(null, '', window.location.pathname)
                
                const returnTo = window.localStorage.getItem('postLoginReturn') || '/'
                window.localStorage.removeItem('postLoginReturn')
                navigate(returnTo, { replace: true })
                return
              }
            } catch (err) {
               console.error('⚠️ [AuthCallback] Falha no setSession:', err)
               // Não lança erro fatal ainda, tenta verificar getSession
            }
          }
        }

        // 3. TENTATIVA SECUNDÁRIA: Verificar Sessão Existente
        // Se o hash já foi processado pelo cliente Supabase ou se já tínhamos sessão
        console.log('🔍 [AuthCallback] Verificando sessão existente...')
        setStatus('Verificando sessão ativa...')
        
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()

        if (sessionError) {
            console.warn('⚠️ [AuthCallback] Erro ao obter sessão:', sessionError)
        }

        if (session) {
          console.log('🚀 [AuthCallback] Sessão encontrada. Redirecionando...')
          navigate('/', { replace: true })
          return
        }

        // 4. TENTATIVA TERCIÁRIA: Contexto de Autenticação (Fallback final)
        // O user pode ter sido atualizado enquanto processávamos
        if (user) {
            console.log('🚀 [AuthCallback] Usuário detectado no contexto (tardia). Redirecionando...')
            navigate('/', { replace: true })
            return
        }

        // Se chegamos aqui, não conseguimos autenticar
        console.error('❌ [AuthCallback] Nenhuma sessão encontrada após todas as tentativas.')
        // Em vez de erro fatal imediato, redirecionar para login após breve aviso se estiver em localhost
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
           setStatus('Redirecionando para login...')
           setTimeout(() => navigate('/auth/login'), 2000)
           setError('Sessão não detectada. Redirecionando para login...')
        } else {
           setError('Não foi possível confirmar seu login. O link pode ter expirado ou o ambiente bloqueou os cookies.')
        }
        
      } catch (err: any) {
        console.error('💥 [AuthCallback] Erro fatal:', err)
        setError(err.message || 'Ocorreu um erro inesperado durante o login.')
      }
    }

    handleAuthCallback()

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [navigate, user])

  // Renderização de Erro com Opções de Recuperação
  if (error) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 text-center">
        <div className="bg-zinc-900 border border-red-500/30 p-8 rounded-2xl max-w-md w-full shadow-2xl shadow-red-900/20">
          <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Falha na Autenticação</h2>
          <p className="text-gray-400 mb-8 text-sm">{error}</p>
          
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Tentar Novamente
            </button>
            <button 
              onClick={() => navigate('/auth/login')}
              className="w-full py-3 bg-transparent border border-white/10 text-white font-medium rounded-lg hover:bg-white/5 transition-colors"
            >
              Voltar para Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-purple-900/20 via-black to-black opacity-50"></div>
      
      <div className="relative z-10 flex flex-col items-center">
        <LoadingSpinner size="large" message="" />
        
        <div className="mt-8 text-center space-y-2">
          <h2 className="text-2xl font-bold text-white tracking-tight">Conectando à Matrix...</h2>
          <p className="text-cyan-400/80 font-mono text-sm animate-pulse">
            {status}
          </p>
        </div>

        {/* Fallback button appears if taking too long (CSS animation delayed) */}
        <div className="mt-12 opacity-0 animate-[fadeIn_1s_ease-in_3s_forwards]">
            <button 
              onClick={() => navigate('/auth/login')}
              className="text-xs text-gray-500 hover:text-white transition-colors underline decoration-gray-700 underline-offset-4"
            >
              Demorando muito? Voltar para login
            </button>
        </div>
      </div>
    </div>
  )
}

export default AuthCallback
